import { CSSProperties, FC, ReactNode, SyntheticEvent, useEffect, useState } from 'react';

import { Box, Skeleton, SxProps, Theme } from '@mui/material';

import {
  ImageResource,
  ImageSizeSpec,
  ResizeFormat,
  useResourceImage,
} from '@/shared/lib/hooks/useResourceImage';

export interface ResourceImageProps {
  resource: ImageResource | null | undefined;
  alt: string;
  size?: ImageSizeSpec;
  format?: ResizeFormat;
  width?: number | string;
  height?: number | string;
  objectFit?: CSSProperties['objectFit'];
  placeholder?: ReactNode | 'skeleton' | 'none';
  fallback?: string | ReactNode;
  fadeIn?: boolean;
  priority?: boolean;
  onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (err: Error) => void;
  sx?: SxProps<Theme>;
  className?: string;
  'data-testid'?: string;
}

const FALLBACK_PROPS = {
  loading: 'lazy',
  decoding: 'async',
} as const;

export const ResourceImage: FC<ResourceImageProps> = ({
  resource,
  alt,
  size = { tier: 'thumb' },
  format,
  width,
  height,
  objectFit = 'cover',
  placeholder = 'skeleton',
  fallback,
  fadeIn = true,
  priority = false,
  onLoad,
  onError,
  sx,
  className,
  'data-testid': dataTestId,
}) => {
  const { url, isLoading, error } = useResourceImage(resource ?? null, {
    size,
    format,
    // Resize to match how the image is actually displayed: a 'contain' box
    // must keep the full source content (no cropping), while 'cover' (and
    // any other object-fit) can safely crop to fill the box.
    fit: objectFit === 'contain' ? 'contain' : 'cover',
  });

  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgLoaded(false);
  }, [url]);

  if (!resource) {
    return renderFallback(fallback, alt, width, height, objectFit, dataTestId, sx, className);
  }

  if (error && !url) {
    return renderFallback(fallback, alt, width, height, objectFit, dataTestId, sx, className);
  }

  if (isLoading || !url) {
    if (placeholder === 'none') return null;
    if (placeholder === 'skeleton') {
      return (
        <Skeleton
          variant="rectangular"
          width={width ?? '100%'}
          height={height ?? '100%'}
          data-testid={dataTestId}
          className={className}
          sx={sx}
        />
      );
    }
    return <>{placeholder}</>;
  }

  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...(priority ? { fetchPriority: 'high' } : { fetchPriority: 'low' })}
      onLoad={(e: SyntheticEvent<HTMLImageElement>) => {
        setImgLoaded(true);
        onLoad?.(e);
      }}
      onError={() => onError?.(new Error('img element failed to load'))}
      data-testid={dataTestId}
      className={className}
      sx={[
        {
          objectFit,
          opacity: fadeIn ? (imgLoaded ? 1 : 0) : 1,
          transition: fadeIn ? 'opacity 120ms ease-out' : undefined,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

const renderFallback = (
  fallback: ResourceImageProps['fallback'],
  alt: string,
  width: ResourceImageProps['width'],
  height: ResourceImageProps['height'],
  objectFit: CSSProperties['objectFit'],
  dataTestId: string | undefined,
  sx: SxProps<Theme> | undefined,
  className: string | undefined,
) => {
  if (!fallback) return null;
  if (typeof fallback === 'string') {
    return (
      <Box
        component="img"
        src={fallback}
        alt={alt}
        width={width}
        height={height}
        {...FALLBACK_PROPS}
        data-testid={dataTestId}
        className={className}
        sx={[{ objectFit }, ...(Array.isArray(sx) ? sx : [sx])]}
      />
    );
  }
  return <>{fallback}</>;
};

export default ResourceImage;
